import type { FC } from 'react'
import type { Mistake } from '@/constants/home'

import { useState } from 'react'
import { RotateCw, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { hand } from '@/lib/fonts'
import { useDragScroll } from '@/hooks/use-drag-scroll'
import { Reveal } from '@/components/kit/reveal'
import { SectionLabel } from '@/components/kit/section-label'
import { SplitText } from '@/components/kit/split-text'
import { mistakes } from '@/constants/home'

const FlipCard: FC<{ mistake: Mistake, index: number }> = ({ mistake, index }) => {
    const [flipped, setFlipped] = useState(false)

    return (
        <li className='w-[min(82vw,320px)] shrink-0 snap-start'>
            <div
                data-flipped={flipped}
                className='flip-card h-[440px]'
            >
                <div className='flip-inner'>
                    <div className='flip-face flex flex-col overflow-hidden rounded-[1.75rem] border border-ink/10 bg-paper p-6'>
                        <div className='flex items-center justify-between'>
                            <span className='inline-flex items-center gap-1.5 rounded-full bg-[#e3262e]/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-[#c2330a]'>
                                <X className='size-3' />
                                The before
                            </span>
                            <span className='font-mono text-[11px] text-stone'>No. {String(index + 1).padStart(2, '0')}</span>
                        </div>
                        <p className='mt-5 font-mono text-[11px] uppercase tracking-[0.14em] text-stone'>{mistake.label}</p>
                        <div className='mt-3 flex flex-1 items-center rounded-2xl bg-[#ececef] p-5'>
                            <p className='font-serif text-[1.65rem] leading-[1.1] text-ink/80'>{mistake.bad.title}</p>
                        </div>
                        <p className='mt-4 text-sm text-stone'>{mistake.bad.detail}</p>
                    </div>

                    <div className='flip-face flip-back flex flex-col overflow-hidden rounded-[1.75rem] bg-ink p-6 text-cream'>
                        <div className='flex items-center justify-between'>
                            <span className='rounded-full bg-ember px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-ink'>The fix</span>
                            <span className='font-mono text-[11px] text-cream/50'>No. {String(index + 1).padStart(2, '0')}</span>
                        </div>
                        <p className='mt-5 font-heading text-[1.85rem] font-bold leading-[1.02] tracking-[-0.035em]'>{mistake.good.title}</p>
                        <p className='mt-3 text-sm text-ember'>{mistake.good.detail}</p>
                        <div className='mt-auto border-t border-cream/15 pt-4'>
                            <p className='font-mono text-[10px] uppercase tracking-[0.14em] text-cream/50'>The lesson</p>
                            <p className={cn(hand.className, 'mt-1 text-[1.6rem] leading-[1.05] text-butter')}>{mistake.lesson}</p>
                        </div>
                    </div>
                </div>
            </div>

            <button
                type='button'
                aria-pressed={flipped}
                onClick={() => setFlipped(value => !value)}
                className='mt-3 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.14em] text-stone transition-colors hover:text-ink'
            >
                <RotateCw className={cn('size-3.5 transition-transform duration-500', flipped && 'rotate-180')} />
                {flipped ? 'Show the before' : 'Show the fix'}
            </button>
        </li>
    )
}

export const MistakesFixed: FC = () => {
    const rowRef = useDragScroll<HTMLUListElement>()

    return (
        <section
            id='mistakes'
            aria-labelledby='mistakes-title'
            className='overflow-hidden py-20 md:py-28'
        >
            <div className='mx-auto max-w-[1400px] px-5 md:px-10'>
                <div className='grid gap-8 lg:grid-cols-12 lg:items-end'>
                    <div className='lg:col-span-7'>
                        <SectionLabel index='10'>Corrections</SectionLabel>
                        <SplitText
                            as='h2'
                            id='mistakes-title'
                            lines={['Mistakes we', 'fixed (politely)']}
                            italic='politely'
                            className='mt-5 text-[clamp(2.75rem,5.6vw,5.25rem)] font-bold leading-[0.9] tracking-[-0.045em]'
                        />
                    </div>
                    <Reveal
                        as='p'
                        delay={150}
                        className='text-lg leading-relaxed text-stone lg:col-span-5'
                    >
                        Real habits we find in almost every first audit. Hover a card, or tap the button underneath, to flip it and see what we did instead.
                    </Reveal>
                </div>
            </div>

            <ul
                ref={rowRef}
                data-cursor='Drag'
                className='no-scrollbar mt-12 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-4 md:gap-5 md:px-10 xl:px-[max(2.5rem,calc((100vw_-_1400px)/2_+_2.5rem))]'
            >
                {mistakes.map((mistake, index) => (
                    <FlipCard
                        key={mistake.label}
                        mistake={mistake}
                        index={index}
                    />
                ))}
            </ul>
        </section>
    )
}
